import { createLink, navigateToCallback } from '../components';
/**
 * Maps header configuration into state-ready format with navigation logic.
 *
 * @param {NavigationInterface} navigationContext - Context used for managing navigation state and transitions.
 * @param {NavigateFunction} navigate - React Router navigation function.
 * @param {Location} location - React Router location object representing the current URL.
 * @param {string} [basename] - Optional base path used for resolving links.
 * @param {HeaderItem[]} [headerItems] - Optional list of header items to be displayed.
 * @param {HeaderItem} [headerPrimaryItem] - Optional primary header item to highlight or pin.
 *
 * @returns {{
 *   headerItems?: GlobalHeaderItem[];
 *   headerPrimaryItem?: GlobalHeaderItem;
 * }} Returns mapped header items and optionally a primary header item with resolved navigation behavior.
 */
export function useHeaderState(navigationContext, navigate, location, basename, headerItems, headerPrimaryItem) {
    return {
        headerItems: headerItems ? headerItems.map((item) => mapHeaderItem(item, navigationContext, navigate, basename)) : undefined,
        headerPrimaryItem: headerPrimaryItem ? mapHeaderItem(headerPrimaryItem, navigationContext, navigate, basename) : undefined,
    };
}
/**
 * Converts a HeaderItem into a GlobalHeaderItem with resolved navigation and menu item callbacks.
 *
 * @param {HeaderItem} item - The header item to map.
 * @param {NavigationInterface} navigationContext - Navigation context for routing control.
 * @param {NavigateFunction} navigate - React Router navigation function used to navigate programmatically.
 * @param {string} [basename] - Optional base path used to prefix route links.
 *
 * @returns {GlobalHeaderItem} Returns the transformed header item with proper navigation handling.
 */
function mapHeaderItem(item, navigationContext, navigate, basename) {
    var _a;
    const { navigateTo, onClick } = item;
    return {
        icon: item.icon,
        label: item.label,
        href: (_a = item.href) !== null && _a !== void 0 ? _a : (item.navigateTo ? createLink([basename !== null && basename !== void 0 ? basename : '/', item.navigateTo]) : undefined),
        onClick: navigateToCallback(navigationContext, navigate, navigateTo, onClick),
        menuItems: item.menuItems
            ? item.menuItems.map((menuItem) => {
                var _a;
                const { navigateTo, onClick } = menuItem;
                return {
                    label: menuItem.label,
                    onClick: (_a = navigateToCallback(navigationContext, navigate, navigateTo, onClick)) !== null && _a !== void 0 ? _a : (() => undefined),
                    disabled: menuItem.disabled,
                    ['data-test']: menuItem['data-test'],
                    ['aria-label']: menuItem['aria-label'],
                };
            })
            : undefined,
        ['data-test']: item['data-test'],
    };
}
